import { css } from "@emotion/css";
import type { PrimaryPageAction } from "@octopusdeploy/design-system-components";
import { EmptyStateNoResults, Pagination, SearchInput, SimpleDataTable } from "@octopusdeploy/design-system-components";
import { ArrowDownToSquareIcon } from "@octopusdeploy/design-system-icons";
import { borderRadius, space, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { PackageResource, Repository, ResourceCollection } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import type { QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import { links } from "@octopusdeploy/portal-routes";
import type * as React from "react";
import { useCallback } from "react";
import { HowToCreateYourPackageDialogLink } from "~/areas/library/components/BuiltInRepository/HowToCreateYourPackageDialogLink";
import { HowToPushYourPackageDialogLink } from "~/areas/library/components/BuiltInRepository/HowToPushYourPackageDialogLink";
import { client } from "~/clientInstance";
import { PortalEmptyStateOnboarding } from "~/components/GettingStarted/PortalEmptyStateOnboarding";
import InternalLink from "~/components/Navigation/InternalLink/InternalLink";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { PageContent } from "~/components/PageContent/PageContent";
import PermissionCheck, { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { useDebouncedExternalState } from "~/hooks/useDebouncedExternalState";
import { useWorkflow } from "~/workflows/useWorkflow";
import { workflows } from "~/workflows/workflows";
import { BuiltInRepositoryPageSidebar } from "./BuiltInRepositorySidebar";
import { supportedPackageTypes } from "./supportedPackageTypes";
interface BuiltInPackageRepositoryPageProps {
    spaceId: string;
    loaderData: BuiltInPackageRepositoryPageLoaderData;
    queryParams: BuiltInPackageRepositoryPageQueryParams;
    setQueryParams: QueryParamValuesSetter<BuiltInPackageRepositoryPageQueryParams>;
}
export interface BuiltInPackageRepositoryPageQueryParams {
    page: number | undefined;
    pageSize: number | undefined;
    filter: string;
}
const defaultPageSize = 30;
const pageSizeOptions = [defaultPageSize, 50, 100];
const pageTitle = "Built-in Package Repository";
export async function builtInPackageRepositoryPageLoader(repository: Repository, queryParams: BuiltInPackageRepositoryPageQueryParams): Promise<BuiltInPackageRepositoryPageLoaderData> {
    return {
        packages: await getPaginatedPackages(repository, queryParams.page, queryParams.pageSize, queryParams.filter),
        totalPackages: (await repository.Feeds.getBuiltInStatus()).TotalPackages,
    };
}
export interface BuiltInPackageRepositoryPageLoaderData {
    packages: ResourceCollection<PackageResource>;
    totalPackages: number;
}
export function BuiltInPackageRepositoryPage({ spaceId, loaderData, queryParams, setQueryParams }: BuiltInPackageRepositoryPageProps) {
    const [debouncedFilter, setDebouncedFilter] = useDebouncedExternalState(queryParams.filter, (value) => setQueryParams({ filter: value, page: 1 }), 200);
    const navigate = useSpaceAwareNavigation();
    const startUploadPackageWorkflow = useWorkflow(workflows.uploadPackageWorkflow);
    const { result: paginatedPackages } = usePaginatedPackagesQuery(loaderData, queryParams);
    const onPageChange = useCallback((page: number) => {
        setQueryParams({ ...queryParams, page });
    }, [queryParams, setQueryParams]);
    const onPageSizeChange = useCallback((pageSize: number) => {
        setQueryParams({ ...queryParams, pageSize: pageSize, page: 1 });
    }, [queryParams, setQueryParams]);
    const uploadPackagePageAction: PrimaryPageAction = {
        type: "button",
        hasPermissions: isAllowed({ permission: Permission.BuiltInFeedPush, project: "*" }),
        label: "Upload package",
        onClick: () => {
            startUploadPackageWorkflow((pkg) => navigate.navigate(links.packageDetailsPage.generateUrl({ spaceId, id: pkg.Id })));
        },
    };
    if (loaderData.totalPackages === 0) {
        return (<>
                <PortalEmptyStateOnboarding title={"Upload or push packaged applications to the built-in package repository"} description={`The built-in repository accepts ${supportedPackageTypes} packages.`} primaryAction={uploadPackagePageAction} actions={[
                { type: "custom", key: "how-to-create-packages", content: <HowToCreateYourPackageDialogLink label={"How to create packages"} asSecondaryButton={true}/> },
                { type: "custom", key: "how-to-push-packages", content: <HowToPushYourPackageDialogLink label={"How to push packages"} asSecondaryButton={true}/> },
            ]}/>
            </>);
    }
    const hideBottomPagination = paginatedPackages.Items.length < 20;
    return (<PageContent header={{ title: pageTitle, primaryAction: uploadPackagePageAction }} filters={{ inputs: [<SearchInput value={debouncedFilter} onChange={setDebouncedFilter} accessibleName={"Search by package name"} placeholder={"Search by name"}/>] }} pagination={{
            ui: (<Pagination label="Packages" totalResults={paginatedPackages.TotalResults} itemsPerPageOptions={pageSizeOptions} selectedItemsPerPage={paginatedPackages.ItemsPerPage} currentPage={queryParams.page ?? 1} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange}/>),
            placement: hideBottomPagination ? "top" : "topAndBottom",
        }} sidebar={<BuiltInRepositoryPageSidebar />}>
            <SidebarBorderedContent>
                {paginatedPackages.TotalResults === 0 ? (<EmptyStateNoResults />) : (<SimpleDataTable data={paginatedPackages.Items} columns={[
                {
                    title: "ID",
                    render: (pkg) => (<InternalLink accessibleName={`View versions for ${pkg.PackageId}`} to={links.packageVersionsPage.generateUrl({ spaceId, packageId: pkg.PackageId })}>
                                        {pkg.PackageId}
                                    </InternalLink>),
                },
                { title: "Highest version", render: (pkg) => <PackageVersionCell spaceId={spaceId} pkg={pkg}/> },
                { title: "Description", render: (pkg) => pkg.Description },
            ]} getRowKey={(pkg) => pkg.Id}/>)}
            </SidebarBorderedContent>
        </PageContent>);
}
interface PackageVersionCellProps {
    spaceId: string;
    pkg: PackageResource;
}
function PackageVersionCell({ spaceId, pkg }: PackageVersionCellProps) {
    return (<div className={packageVersionCellStyles}>
            <PermissionCheck permission={Permission.BuiltInFeedDownload} project="*">
                <a aria-label={`Download package ${pkg.Id} version ${pkg.Version}`} href={client.resolve(pkg.Links["Raw"])}>
                    <ArrowDownToSquareIcon size={"font-size"}/>
                </a>
                &nbsp;
            </PermissionCheck>
            <InternalLink accessibleName={`View package details for ${pkg.Id} version ${pkg.Version}`} to={links.packageDetailsPage.generateUrl({ spaceId, id: pkg.Id })}>
                {pkg.Version}
            </InternalLink>
        </div>);
}
const packageVersionCellStyles = css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: space[2],
});
async function getPaginatedPackages(repository: Repository, page: number = 1, pageSize: number = defaultPageSize, filter: string = "") {
    return repository.Packages.list({
        skip: (page - 1) * pageSize,
        take: pageSize,
        latest: true,
        filter,
    });
}
function usePaginatedPackagesQuery(initialResult: BuiltInPackageRepositoryPageLoaderData, queryParams: BuiltInPackageRepositoryPageQueryParams) {
    const { page, pageSize, filter } = queryParams;
    return useQuery((repository) => getPaginatedPackages(repository, page, pageSize, filter), [page, pageSize, filter], "Get paginated packages", { initialResult: initialResult.packages });
}
export function SidebarBorderedContent({ children }: {
    children: React.ReactNode;
}) {
    return <div className={sidebarBorderedContent}>{children}</div>;
}
const sidebarBorderedContent = css({
    border: `1px solid ${themeTokens.color.border.primary}`,
    borderRadius: borderRadius.medium,
    overflow: "clip",
    borderRight: "none",
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    minHeight: "100%",
});
